// src/components/shared/forms/EntityContactForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  Button,
  Box,
  IconButton,
  CardContent,
  FormControlLabel,
  Radio,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  LocationCity as CityIcon,
  Public as CountryIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

// Import UI components
import MagicTextField from '../../ui/FormFields/MagicTextField';
import MagicSelect from '../../ui/FormFields/MagicSelect';
import MagicCard from '../../ui/MagicCard';
import { FormSection } from '../../../design-system/components/forms';

/**
 * EntityContactForm - A reusable contact information form for different entity types
 * 
 * @param {Object} props Component props
 * @param {Object} props.data The entity data containing contact information
 * @param {Function} props.onChange Event-based change handler
 * @param {Function} props.onNestedChange Nested field change handler
 * @param {Function} props.onContactsChange Dedicated contacts array change handler
 * @param {Function} props.setFieldValue Formik-style field setter
 * @param {Object} props.errors Validation errors
 * @param {Object} props.touched Touched fields
 * @param {Boolean} props.showAddress Whether to show address section
 * @param {Boolean} props.showContacts Whether to show contacts section
 * @param {String} props.addressFieldName Field name for address in data object
 * @param {String} props.contactsFieldName Field name for contacts in data object
 * @param {Object} props.contactFieldLabels Custom labels for contact fields
 * @param {Array} props.countryOptions Options for country dropdown
 * @param {Object} props.contactConfig Configuration for contact form display
 * @param {Boolean} props.requirePrimaryContact Whether a primary contact is required
 * @param {Object} props.sectionTitles Custom titles for form sections
 */
const EntityContactForm = ({ 
  data = {},
  onChange,
  onNestedChange,
  onContactsChange,
  setFieldValue,
  errors = {},
  touched = {},
  showAddress = true,
  showContacts = true,
  addressFieldName = 'address',
  contactsFieldName = 'contacts',
  contactFieldLabels = {
    name: 'Contact Name',
    position: 'Position / Title',
    email: 'Email Address',
    phone: 'Phone Number'
  },
  countryOptions = DEFAULT_COUNTRY_OPTIONS,
  contactConfig = {
    primaryLabel: 'Primary Contact',
    setPrimaryLabel: 'Set as Primary Contact',
    addLabel: 'Add Contact',
    contactIndexLabel: 'Contact'
  },
  requirePrimaryContact = true,
  sectionTitles = {
    address: 'Address Information',
    contacts: 'Contact Persons'
  }
}) => {
  const theme = useTheme();
  
  // Get the address and contacts data from the proper fields
  const addressData = data[addressFieldName] || {};
  const contactsData = data[contactsFieldName] || [];
  
  // Handle address field change with support for multiple API patterns
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    
    // Support nested change handler
    if (onNestedChange) {
      onNestedChange(addressFieldName, name, value);
    } 
    // Support formik-style setter
    else if (setFieldValue) {
      setFieldValue(`${addressFieldName}.${name}`, value);
    }
    // Support event-based handler
    else if (onChange && typeof onChange === 'function' && onChange.length !== 2) {
      onChange(e);
    }
    // Support name/value handler
    else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${addressFieldName}.${name}`, value);
    }
  };
  
  // Handle select field change (for country)
  const handleSelectChange = (value, fieldName) => {
    if (onNestedChange) {
      onNestedChange(addressFieldName, fieldName, value);
    } else if (setFieldValue) {
      setFieldValue(`${addressFieldName}.${fieldName}`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${addressFieldName}.${fieldName}`, value);
    }
  };
  
  // Handle adding a new contact
  const handleAddContact = () => {
    const newContact = {
      name: '',
      position: '',
      email: '',
      phone: '',
      isPrimary: contactsData.length === 0 && requirePrimaryContact,
    };
    
    const updatedContacts = [...contactsData, newContact];
    
    // Support specialized contacts handler
    if (onContactsChange) {
      onContactsChange(updatedContacts);
    } 
    // Support formik
    else if (setFieldValue) {
      setFieldValue(contactsFieldName, updatedContacts);
    }
    // Support name/value handler
    else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(contactsFieldName, updatedContacts);
    }
  };
  
  // Handle deleting a contact
  const handleDeleteContact = (index) => {
    const updatedContacts = [...contactsData];
    const isDeletingPrimary = updatedContacts[index]?.isPrimary;
    
    updatedContacts.splice(index, 1);
    
    // If we deleted the primary contact and require a primary, make the first contact primary
    if (requirePrimaryContact && updatedContacts.length > 0 && isDeletingPrimary) {
      updatedContacts[0].isPrimary = true;
    }
    
    // Support multiple API patterns
    if (onContactsChange) {
      onContactsChange(updatedContacts);
    } else if (setFieldValue) {
      setFieldValue(contactsFieldName, updatedContacts);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(contactsFieldName, updatedContacts);
    }
  };
  
  // Handle changing a contact field
  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...contactsData];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    
    // Support multiple API patterns
    if (onContactsChange) {
      onContactsChange(updatedContacts);
    } else if (setFieldValue) {
      setFieldValue(contactsFieldName, updatedContacts);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(contactsFieldName, updatedContacts);
    }
  };
  
  // Handle setting a contact as primary
  const handleSetPrimary = (index) => {
    const updatedContacts = contactsData.map((contact, i) => ({
      ...contact,
      isPrimary: i === index,
    }));
    
    // Support multiple API patterns
    if (onContactsChange) {
      onContactsChange(updatedContacts);
    } else if (setFieldValue) {
      setFieldValue(contactsFieldName, updatedContacts);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(contactsFieldName, updatedContacts);
    }
  };
  
  return (
    <Grid container spacing={4}>
      {/* Address Section */}
      {showAddress && (
        <Grid item xs={12}>
          <FormSection
            icon={<LocationIcon />}
            title={sectionTitles.address}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MagicTextField
                  label="Street Address"
                  name="street"
                  value={addressData.street || ''}
                  onChange={handleAddressChange}
                  fullWidth
                  placeholder="Enter street address"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <MagicTextField
                  label="City"
                  name="city"
                  value={addressData.city || ''}
                  onChange={handleAddressChange}
                  fullWidth
                  placeholder="Enter city name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CityIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <MagicTextField
                  label="State/Province"
                  name="state"
                  value={addressData.state || ''}
                  onChange={handleAddressChange}
                  fullWidth
                  placeholder="Enter state or province"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <MagicTextField
                  label="Zip/Postal Code"
                  name="zipCode"
                  value={addressData.zipCode || ''}
                  onChange={handleAddressChange}
                  fullWidth
                  placeholder="Enter postal code"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <MagicSelect
                  name="country"
                  label="Country"
                  value={addressData.country || ''}
                  onChange={(value) => handleSelectChange(value, 'country')}
                  options={countryOptions}
                  placeholder="Select country"
                  magical={true}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CountryIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>
        </Grid>
      )}
      
      {/* Contacts Section */}
      {showContacts && (
        <Grid item xs={12}>
          <FormSection
            icon={<PersonIcon />}
            title={sectionTitles.contacts}
          >
            {contactsData.map((contact, index) => (
              <MagicCard
                key={index}
                magical={contact.isPrimary}
                level={contact.isPrimary ? 2 : 1}
                sx={{ 
                  mb: 3, 
                  position: 'relative',
                  border: contact.isPrimary 
                    ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` 
                    : undefined,
                }}
              >
                {contact.isPrimary && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: 20,
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                      borderRadius: 8,
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      zIndex: 1,
                    }}
                  >
                    {contactConfig.primaryLabel}
                  </Box>
                )}
                
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1">
                      {contactConfig.contactIndexLabel} {index + 1}
                    </Typography>
                    {contactsData.length > 1 && (
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => handleDeleteContact(index)}
                        aria-label="delete contact"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <MagicTextField
                        label={contactFieldLabels.name}
                        value={contact.name || ''}
                        onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                        fullWidth
                        required={index === 0 && requirePrimaryContact}
                        placeholder={`Enter ${contactFieldLabels.name.toLowerCase()}`}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          )
                        }}
                        error={index === 0 && requirePrimaryContact && touched?.[contactsFieldName]?.[0]?.name && !contact.name}
                        helperText={index === 0 && requirePrimaryContact && touched?.[contactsFieldName]?.[0]?.name && !contact.name 
                          ? 'Primary contact name is required' 
                          : ''
                        }
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <MagicTextField
                        label={contactFieldLabels.position}
                        value={contact.position || ''}
                        onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                        fullWidth
                        placeholder={`Enter ${contactFieldLabels.position.toLowerCase()}`}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WorkIcon color="action" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <MagicTextField
                        label={contactFieldLabels.email}
                        value={contact.email || ''}
                        onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                        fullWidth
                        placeholder={`Enter ${contactFieldLabels.email.toLowerCase()}`}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon color="action" />
                            </InputAdornment>
                          )
                        }}
                        error={contact.email && !/^\S+@\S+\.\S+$/.test(contact.email)}
                        helperText={contact.email && !/^\S+@\S+\.\S+$/.test(contact.email) 
                          ? 'Invalid email format' 
                          : ''
                        }
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <MagicTextField
                        label={contactFieldLabels.phone}
                        value={contact.phone || ''}
                        onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                        fullWidth
                        placeholder={`Enter ${contactFieldLabels.phone.toLowerCase()}`}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon color="action" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                  
                  {!contact.isPrimary && contactsData.length > 1 && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleSetPrimary(index)}
                      >
                        {contactConfig.setPrimaryLabel}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </MagicCard>
            ))}
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddContact}
            >
              {contactConfig.addLabel}
            </Button>
          </FormSection>
        </Grid>
      )}
    </Grid>
  );
};

// Default country options with MENA region focus
const DEFAULT_COUNTRY_OPTIONS = [
  { value: 'Egypt', label: 'Egypt' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'UAE', label: 'United Arab Emirates' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'Kuwait', label: 'Kuwait' },
  { value: 'Bahrain', label: 'Bahrain' },
  { value: 'Oman', label: 'Oman' },
  { value: 'Jordan', label: 'Jordan' },
  { value: 'Lebanon', label: 'Lebanon' },
  { value: 'Morocco', label: 'Morocco' },
  { value: 'Tunisia', label: 'Tunisia' },
  { value: 'Algeria', label: 'Algeria' },
  { value: 'Libya', label: 'Libya' },
  { value: 'Iraq', label: 'Iraq' },
  { value: 'Yemen', label: 'Yemen' },
  { value: 'USA', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'France', label: 'France' },
  { value: 'Germany', label: 'Germany' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Australia', label: 'Australia' },
  { value: 'China', label: 'China' },
  { value: 'India', label: 'India' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'Other', label: 'Other' }
];

export default EntityContactForm;